import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /**
   * Opt OUT of the automatic tooltip an icon button gets.
   *
   * For the few whose meaning is already on screen beside them, or which sit
   * inside something that provides its own hover treatment.
   */
  noTooltip?: boolean;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AN ICON BUTTON EXPLAINS ITSELF
 *
 * Every icon-only button in this product carries an `aria-label`, because a
 * button whose whole content is a glyph is unusable with a screen reader
 * without one. A sighted person has exactly the same problem and no such
 * affordance: a row of four grey glyphs is a guess until you click one.
 *
 * So the label a button already has becomes a tooltip, here, once — rather
 * than each call site remembering to wrap itself. That wrapping is the thing
 * that does not happen: `IconTooltip` existed for months and had been applied
 * to one screen out of fifteen.
 *
 * ── Conditions ────────────────────────────────────────────────────────────
 * Only `size="icon"` (a labelled button with visible text needs no tooltip),
 * only with an `aria-label` to show, and never when `noTooltip` is set. The
 * DOM is unchanged: `TooltipTrigger asChild` renders the button itself rather
 * than adding a wrapper, so nothing about layout or focus moves.
 * ═══════════════════════════════════════════════════════════════════════════
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className, variant, size, asChild = false, loading = false,
      noTooltip = false, children, disabled, ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const label = props['aria-label'];
    const tooltip = size === 'icon' && !noTooltip && typeof label === 'string' && label.length > 0;

    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" aria-hidden />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );

    if (!tooltip) return button;

    /* 200ms, not the 700ms default: these sit in rows, and moving along four
       icons at the default delay shows nothing at all. */
    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
